/**
 * Navigation Component (Material UI)
 * Clean, professional navigation following MUI marketing page template style
 */
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Menu,
  MenuItem,
  Collapse,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MonetizationOn as MonetizationOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useCustomTranslation } from '../../hooks';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { calculators, calculatorCategories, type Calculator } from '../../data/calculators';

export default function NavigationMUI() {
  const { t } = useCustomTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [calculatorsMenuAnchor, setCalculatorsMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileExpandCalculators, setMobileExpandCalculators] = useState(false);

  // Group calculators by category
  const groupedCalculators = calculators.reduce((acc, calc) => {
    if (!acc[calc.category]) {
      acc[calc.category] = [];
    }
    acc[calc.category].push(calc);
    return acc;
  }, {} as Record<string, Calculator[]>);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCalculatorsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalculatorsMenuAnchor(event.currentTarget);
  };

  const handleCalculatorsMenuClose = () => {
    setCalculatorsMenuAnchor(null);
  };

  const handleMobileLinkClick = () => {
    setMobileOpen(false);
    setMobileExpandCalculators(false);
  };

  // Desktop Calculators Menu
  const calculatorsMenu = (
    <Menu
      anchorEl={calculatorsMenuAnchor}
      open={Boolean(calculatorsMenuAnchor)}
      onClose={handleCalculatorsMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 800,
          maxWidth: '90vw',
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3 }}>
          {Object.entries(groupedCalculators).map(([category, calcs]) => (
            <Box key={category}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  mb: 1,
                  display: 'block',
                }}
              >
                {calculatorCategories[category as Calculator['category']].icon}{' '}
                {t(calculatorCategories[category as Calculator['category']].titleKey)}
              </Typography>
              <Stack spacing={0.5}>
                {calcs.map((calc) => (
                  <MenuItem
                    key={calc.id}
                    component={RouterLink}
                    to={calc.path}
                    onClick={handleCalculatorsMenuClose}
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t(calc.titleKey)}
                    </Typography>
                  </MenuItem>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Menu>
  );

  // Mobile Drawer
  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MonetizationOnIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            PayWise365
          </Typography>
        </Box>
      </Box>
      <List>
        <ListItemButton onClick={() => setMobileExpandCalculators(!mobileExpandCalculators)}>
          <ListItemText primary={t('nav.calculators')} primaryTypographyProps={{ fontWeight: 600 }} />
          {mobileExpandCalculators ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
        <Collapse in={mobileExpandCalculators} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {Object.entries(groupedCalculators).map(([category, calcs]) => (
              <Box key={category}>
                <ListItemButton sx={{ pl: 4 }}>
                  <ListItemText
                    primary={t(calculatorCategories[category as Calculator['category']].titleKey)}
                    primaryTypographyProps={{ variant: 'overline', fontWeight: 700 }}
                  />
                </ListItemButton>
                {calcs.map((calc) => (
                  <ListItemButton
                    key={calc.id}
                    component={RouterLink}
                    to={calc.path}
                    onClick={handleMobileLinkClick}
                    sx={{ pl: 6 }}
                  >
                    <ListItemText primary={t(calc.titleKey)} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>
        </Collapse>

        <Divider sx={{ my: 1 }} />

        <ListItemButton
          component={RouterLink}
          to="/blog"
          onClick={handleMobileLinkClick}
          selected={location.pathname.startsWith('/blog')}
        >
          <ListItemText primary={t('nav.blog')} primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>

        <ListItemButton
          component={RouterLink}
          to="/about"
          onClick={handleMobileLinkClick}
          selected={location.pathname === '/about'}
        >
          <ListItemText primary={t('nav.about')} primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 }, px: { xs: 1, sm: 2 } }}>
            {/* Logo */}
            <Button
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mr: { xs: 1, sm: 4 },
                color: 'text.primary',
                textTransform: 'none',
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: 'transparent',
                  opacity: 0.8,
                },
              }}
            >
              <MonetizationOnIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                PayWise365
              </Typography>
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Stack direction="row" spacing={0} sx={{ mr: 2, gap: 0.5 }}>
                <Button
                className='btn-secondary'
                  onClick={handleCalculatorsMenuOpen}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: 'transparent',
                    minWidth: 100,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {t('nav.calculators')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/blog"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: location.pathname.startsWith('/blog') ? 'action.selected' : 'transparent',
                    minWidth: 100,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {t('nav.blog')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/about"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: location.pathname === '/about' ? 'action.selected' : 'transparent',
                    minWidth: 100,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {t('nav.about')}
                </Button>
              </Stack>
            )}

            {/* Language Switcher */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <LanguageSwitcher />
            </Box>

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                edge="end"
                onClick={handleDrawerToggle}
                aria-label="open drawer"
                sx={{
                  ml: 1,
                  color: 'text.primary',
                  borderRadius: 1,
                  minWidth: 'auto',
                  width: 40,
                  height: 40,
                  padding: 0,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {calculatorsMenu}

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
